import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useWildfireSearch } from '../../hooks/useWildfireSearch';
import './WildfireAutocomplete.scss';

interface WildfireAutocompleteProps {
  value: string;
  onChange: (uuid: string, name?: string) => void;
  disabled?: boolean;
  placeholder?: string;
  currentYearOnly?: boolean;
  className?: string;
}

const WildfireAutocomplete: React.FC<WildfireAutocompleteProps> = ({
  value,
  onChange,
  disabled,
  placeholder,
  currentYearOnly,
  className,
}) => {
  const { results, loading, search, clearResults } = useWildfireSearch({ currentYearOnly });
  const [query, setQuery] = useState('');
  const [selectedName, setSelectedName] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Close dropdown on outside click.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // External clear: keep our display in sync if value is reset to empty.
  useEffect(() => {
    if (!value) {
      setSelectedName('');
      setQuery('');
    }
  }, [value]);

  const handleInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value;
    setQuery(q);
    setOpen(true);
    if (selectedName && q !== selectedName) {
      // User started editing an existing pick; clear the value until they pick again.
      setSelectedName('');
      onChange('');
    }
    search(q);
  }, [search, selectedName, onChange]);

  const handlePick = useCallback((id: string, name: string) => {
    setSelectedName(name);
    setQuery(name);
    setOpen(false);
    clearResults();
    onChange(id, name);
  }, [onChange, clearResults]);

  const handleClear = useCallback(() => {
    setSelectedName('');
    setQuery('');
    setOpen(false);
    clearResults();
    onChange('');
  }, [onChange, clearResults]);

  return (
    <div ref={wrapperRef} className={`wildfire-autocomplete ${className || ''}`}>
      <input
        type="text"
        value={query}
        onChange={handleInput}
        onFocus={() => { if (results.length > 0) setOpen(true); }}
        disabled={disabled}
        placeholder={placeholder}
        className="wildfire-autocomplete__input"
      />
      {(query || selectedName) && !disabled && (
        <button
          type="button"
          className="wildfire-autocomplete__clear"
          onClick={handleClear}
          title="Clear"
          aria-label="Clear fire selection"
        >
          ×
        </button>
      )}
      {open && (results.length > 0 || loading) && (
        <ul className="wildfire-autocomplete__list">
          {loading && <li className="wildfire-autocomplete__loading">Searching…</li>}
          {!loading && results.map((r) => (
            <li
              key={r.id}
              className="wildfire-autocomplete__item"
              onMouseDown={(e) => { e.preventDefault(); handlePick(r.id, r.name); }}
            >
              {r.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default WildfireAutocomplete;
